import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { axiosInstance } from '@/App';
import { useFM } from '@/context/FMContext';
import { getStatusColors } from '@/lib/fmUtils';
import StatusBadge from '@/components/fm/StatusBadge';

/**
 * FMPortfolio - Grid of gym mini-cards showing all assigned gyms.
 * Each card shows gym name, status color, key metric, and is tappable.
 * Tapping a card sets selected gym in FMContext and navigates to KPI dashboard.
 */
export default function FMPortfolio() {
  const navigate = useNavigate();
  const { setSelectedGymId, gyms: contextGyms } = useFM();
  const [portfolio, setPortfolio] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch portfolio data
  useEffect(() => {
    const fetchPortfolio = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axiosInstance.get('/fm/portfolio');
        const data = response.data.gyms || response.data || [];
        setPortfolio(data);
      } catch (err) {
        console.error('Failed to fetch portfolio:', err);
        setError(err.response?.data?.detail || 'Failed to load portfolio');
        // Fall back to context gyms if API fails
        if (contextGyms && contextGyms.length > 0) {
          setPortfolio(contextGyms);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPortfolio();
  }, [contextGyms]);

  const handleGymTap = (gym) => {
    const gymId = gym.gym_id || gym._id;
    setSelectedGymId(gymId);
    navigate('/fm/kpi');
  };

  // Status summary
  const statusCounts = portfolio.reduce(
    (acc, gym) => {
      const status = gym.status || 'UNKNOWN';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    },
    {}
  );

  const statusSummaryParts = [];
  if (statusCounts.GREEN) statusSummaryParts.push(`${statusCounts.GREEN} Green`);
  if (statusCounts.YELLOW) statusSummaryParts.push(`${statusCounts.YELLOW} Yellow`);
  if (statusCounts.RED) statusSummaryParts.push(`${statusCounts.RED} Red`);

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-32 bg-slate-200 rounded animate-pulse" />
        <div className="h-4 w-48 bg-slate-100 rounded animate-pulse" />
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="rounded-xl p-4 border bg-white shadow-sm">
            <div className="h-6 w-40 bg-slate-200 rounded animate-pulse mb-2" />
            <div className="h-4 w-24 bg-slate-100 rounded animate-pulse mb-2" />
            <div className="h-4 w-56 bg-slate-100 rounded animate-pulse" />
          </div>
        ))}
      </div>
    );
  }

  if (error && portfolio.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-sm text-red-500">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 text-sm text-blue-500 underline"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Page Header */}
      <div>
        <h2 className="text-xl font-bold text-slate-800">My Gyms</h2>
        <p className="text-sm text-slate-500 mt-0.5">
          {portfolio.length} gym{portfolio.length !== 1 ? 's' : ''} assigned
        </p>
        {statusSummaryParts.length > 0 && (
          <p className="text-xs text-slate-400 mt-1">
            {statusSummaryParts.join(', ')}
          </p>
        )}
      </div>

      {/* Gym Mini-cards */}
      <div className="space-y-3">
        {portfolio.map((gym) => {
          const gymId = gym.gym_id || gym._id;
          const status = gym.status || 'UNKNOWN';
          const colors = getStatusColors(status);
          const keyMetric = gym.key_metric || gym.biggest_gap || gym.achievement || null;

          return (
            <button
              key={gymId}
              onClick={() => handleGymTap(gym)}
              className={`w-full text-left rounded-xl p-4 border shadow-sm transition-all active:scale-[0.98] ${colors.bgLight || colors.bg} ${colors.border}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-lg text-slate-800 truncate">
                      {gym.name || gym.gym_name || gymId}
                    </h3>
                    <StatusBadge status={status} size="md" />
                  </div>
                  {keyMetric && (
                    <p className="text-sm text-slate-600 truncate">{keyMetric}</p>
                  )}
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0 ml-2" />
              </div>
            </button>
          );
        })}
      </div>

      {portfolio.length === 0 && (
        <div className="text-center py-12">
          <p className="text-sm text-slate-400">No gyms assigned to you yet.</p>
        </div>
      )}
    </div>
  );
}
